import { Icon, Icons } from '@/components/icon';
import { useTranslation } from '@/hooks/use-translation';
import { cn } from '@/lib/utils';
import { useState } from 'react';
import { Combobox, ComboboxGroup, ComboboxItem } from '../ui/combobox';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { P3 } from '../ui/text';

export type SortOrder = 'asc' | 'desc';

export interface SortColumn {
  value: string;
  label: string;
  icon?: keyof typeof Icons;
}

interface SortButtonProps {
  field: string;
  order: SortOrder;
  onSortChange: (field: string, order: SortOrder) => void;
  columns: SortColumn[];
  className?: string;
}

export function SortButton({ field, order, onSortChange, columns, className }: SortButtonProps) {
  const { t } = useTranslation('desktop_ui');
  const [isOpen, setIsOpen] = useState(false);

  // Pre-compute translations
  const sortedByText = t('components.table.sort.sortedBy');
  const ascendingText = t('components.table.sort.ascending');
  const descendingText = t('components.table.sort.descending');
  const searchAttributesText = t('components.table.sort.searchAttributes');

  // Find current column for display
  const currentColumn = columns.find((col) => col.value === field);
  const currentLabel = currentColumn?.label ?? field;
  const currentIcon = currentColumn?.icon;

  const handleFieldChange = (newField: string) => {
    onSortChange(newField, order);
  };

  const handleOrderChange = (newOrder: SortOrder) => {
    onSortChange(field, newOrder);
  };

  // Get the correct sort icon based on order
  const sortIcon = order === 'desc' ? Icons.SortAscending : Icons.SortDescending;

  // Collapsed trigger button
  const collapsedTrigger = (
    <button
      type="button"
      className={cn(
        'flex h-7 cursor-pointer items-center gap-1 rounded-full border border-primary-10 bg-primary-3 hover:bg-primary-5 px-2 outline-none',
        'data-[state=open]:bg-primary-7',
        className,
      )}
    >
      <Icon name={sortIcon} className="text-m text-primary-70" />
      <P3 className="text-primary-80">{sortedByText}</P3>
      <P3 className="font-semibold text-primary-100">{currentLabel}</P3>
      <Icon name={Icons.ChevronDown} className="text-s text-primary-40" />
    </button>
  );

  // Field selector dropdown trigger
  const fieldSelectorTrigger = (
    <button
      type="button"
      className="flex h-[30px] w-[220px] cursor-pointer items-center justify-between rounded-2xl border border-primary-10 bg-white px-3 outline-none hover:bg-primary-3 data-[state=open]:bg-primary-3"
    >
      <div className="flex items-center gap-1">
        <Icon
          name={currentIcon ? Icons[currentIcon] : Icons.Dash}
          className="text-s text-primary-70"
        />
        <P3 className="text-primary-100">{currentLabel}</P3>
      </div>
      <Icon name={Icons.ChevronDown} className="text-s text-primary-40" />
    </button>
  );

  // Direction selector options
  const directionOptions: Array<{ value: SortOrder; label: string; icon: Icons }> = [
    { value: 'desc', label: descendingText, icon: Icons.SortAscending },
    { value: 'asc', label: ascendingText, icon: Icons.SortDescending },
  ];

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>{collapsedTrigger}</PopoverTrigger>
      <PopoverContent
        align="start"
        className="flex h-12 w-auto max-w-[380px] gap-1 rounded-2xl border border-primary-5 bg-white p-2 shadow-md"
      >
        {/* Field Selector */}
        <Combobox
          variant="filter"
          searchPlaceholder={searchAttributesText}
          options={columns}
          value={field}
          onValueChange={handleFieldChange}
          trigger={fieldSelectorTrigger}
        >
          {({ filteredOptions }) =>
            filteredOptions.length > 0 ? (
              <ComboboxGroup variant="filter">
                {filteredOptions.map((option) => {
                  const colOption = option as SortColumn;
                  const isSelected = option.value === field;
                  return (
                    <ComboboxItem key={option.value} option={option} variant="filter">
                      {(opt, _isSelected) => (
                        <div className="flex w-full items-center justify-between">
                          <div className="flex items-center gap-1">
                            <Icon
                              name={colOption.icon ? Icons[colOption.icon] : Icons.Dash}
                              className="text-s text-primary-70"
                            />
                            <P3 className="text-primary-100">{opt.label}</P3>
                          </div>
                          {isSelected && (
                            <Icon
                              name={Icons.CircleCheck}
                              className="text-m text-highlight-default"
                            />
                          )}
                        </div>
                      )}
                    </ComboboxItem>
                  );
                })}
              </ComboboxGroup>
            ) : null
          }
        </Combobox>

        {/* Direction Selector */}
        <Select value={order} onValueChange={(value) => handleOrderChange(value as SortOrder)}>
          <SelectTrigger
            showChevron={false}
            className="flex h-[30px] w-[140px] cursor-pointer items-center justify-between rounded-2xl border border-primary-10 bg-white px-3 outline-none hover:bg-primary-3 data-[state=open]:bg-primary-3 shadow-none focus:ring-0"
          >
            <SelectValue>
              <div className="flex items-center gap-1">
                <Icon name={sortIcon} className="text-s text-primary-70" />
                <P3 className="text-primary-100">
                  {order === 'desc' ? descendingText : ascendingText}
                </P3>
              </div>
            </SelectValue>
            <Icon name={Icons.ChevronDown} className="text-s text-primary-40" />
          </SelectTrigger>
          <SelectContent
            showChevron={false}
            className="w-[140px] min-w-0 rounded-2xl border border-primary-5 bg-white [&_[data-radix-select-viewport]]:p-2 shadow-md [&_[data-slot=select-viewport]]:space-y-1"
          >
            {directionOptions.map((option) => (
              <SelectItem
                key={option.value}
                value={option.value}
                className="h-6 cursor-pointer rounded-md px-1 py-0 text-s font-medium text-primary-100 outline-none data-[highlighted]:bg-primary-3"
              >
                <div className="flex items-center gap-1 pr-6">
                  <Icon name={option.icon} className="text-s text-primary-70" />
                  <P3 className="text-primary-100">{option.label}</P3>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </PopoverContent>
    </Popover>
  );
}
